import { HTMLAttributes } from 'react';

import styled from 'styled-components';

import { Spacing, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';
import { Transient } from '@utils/types';

export const CARD_VARIANTS = [
  'orange',
  'purple',
  'green',
  'red',
  'blue',
  'default',
  'gray',
  'transparent',
  'indigo',
  'indigo-dark',
  'gradient',
] as const;

export type CardVariant = (typeof CARD_VARIANTS)[number];

type CardPropsBase = HTMLAttributes<HTMLDivElement> & {
  variant?: CardVariant;
  interactive?: boolean;
  isActive?: boolean;
  dataCy?: string;
  hasBorder?: boolean;
  dataTestId?: string;
};

type CardPropsSpace = {
  space: Spacing;
};

export type CardPropsSpaceSplit = {
  spaceX: Spacing;
  spaceY: Spacing;
};

export type CardProps = CardPropsBase & (CardPropsSpace | CardPropsSpaceSplit);

export function Card(props: CardProps) {
  const {
    children,
    variant = 'default',
    interactive = false,
    isActive = false,
    hasBorder = true,
    style,
    dataCy,
    dataTestId,
    ...rest
  } = props;

  return (
    <StyledCard
      $interactive={interactive}
      $hasBorder={hasBorder}
      data-variant={variant}
      data-cy={dataCy}
      data-testid={dataTestId}
      style={{
        '--card-space':
          'space' in props
            ? theme.space[props.space]
            : `${theme.space[props.spaceY]} ${theme.space[props.spaceX]}`,
        ...(isActive && {
          boxShadow: theme.shadow.focus,
          '--shadow-focus-width': pxToRem(4),
          '--shadow-focus-gap': 0,
          marginTop: pxToRem(-1),
          marginBottom: pxToRem(-1),
          marginLeft: pxToRem(-1),
          marginRight: pxToRem(-1),
          border: `${pxToRem(2)} solid ${theme.color.elementBlue100}`,
          '--shadow-focus-color': theme.color.blueTrans20,
          transition: transitionStandard('border-color', 'box-shadow'),
        }),
        ...style,
      }}
      {...rest}
    >
      {children}
    </StyledCard>
  );
}

export const StyledCard = styled.div<Transient<CardProps, 'interactive' | 'hasBorder'>>((p) => ({
  borderRadius: pxToRem(4),
  ...(p.$hasBorder && {
    border: `${pxToRem(1)} solid`,
    borderRadius: pxToRem(8),
  }),
  transition: transitionStandard('background', 'border-color', 'box-shadow'),
  position: 'relative',

  padding: 'var(--card-space)',

  ...(p.$interactive && {
    ':hover': {
      transition: transitionStandard('all'),
      cursor: 'pointer',
    },
  }),

  '&[data-variant="orange"]': {
    borderColor: theme.color.elementOrange100,
    color: theme.color.elementOrange100,
    backgroundColor: theme.color.orangeTrans10,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.orangeTrans20,
      },
    }),
  },

  '&[data-variant="purple"]': {
    borderColor: theme.color.elementPurple100,
    color: theme.color.elementPurple100,
    backgroundColor: theme.color.backgroundLightDarkHover,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.purpleTrans20,
      },
    }),
  },

  '&[data-variant="green"]': {
    borderColor: theme.color.elementGreen100,
    color: theme.color.elementGreen100,
    backgroundColor: theme.color.greenTrans10,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.greenTrans20,
      },
    }),
  },

  '&[data-variant="indigo"]': {
    borderColor: theme.color.elementIndigo100,
    color: theme.color.elementIndigo100,
    backgroundColor: theme.color.indigoTrans5,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.indigoTrans20,
      },
    }),
  },

  '&[data-variant="indigo-dark"]': {
    borderColor: theme.color.elementIndigo100,
    color: theme.color.elementWhiteInvert,
    backgroundColor: theme.color.elementIndigo100,
  },

  '&[data-variant="red"]': {
    borderColor: theme.color.elementRed100,
    color: theme.color.elementRed100,
    backgroundColor: theme.color.redTrans10,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.redTrans20,
      },
    }),
  },

  '&[data-variant="blue"]': {
    borderColor: theme.color.elementBlue100,
    color: theme.color.elementBlue100,
    backgroundColor: theme.color.blueTrans10,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.blueTrans20,
      },
    }),
  },

  '&[data-variant="default"]': {
    borderColor: theme.color.border10,
    color: theme.color.textPrimary,
    backgroundColor: theme.color.backgroundLightDark,
    ...(p.$interactive && {
      ':hover': {
        backgroundColor: theme.color.backgroundLightDarkHover,
      },
    }),
  },

  '&[data-variant="gray"]': {
    borderColor: theme.color.border10,
    color: theme.color.textSecondary,
    backgroundColor: theme.color.uiTrans5,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.border20,
        backgroundColor: theme.color.uiTrans10,
      },
    }),
  },
  '&[data-variant="transparent"]': {
    borderColor: theme.color.border10,
    color: theme.color.textPrimary,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.border20,
        backgroundColor: theme.color.uiTrans5,
      },
    }),
  },
  '&[data-variant="gradient"]': {
    borderRadius: pxToRem(8),
    border: `1px solid ${theme.color.purple400}`,
    color: 'white',
    backgroundColor: theme.color.purple400,
    backgroundImage: `radial-gradient(ellipse 100% 100% at 100% 50%, ${theme.color.violet100} -200%, rgba(18, 185, 211, 0.04) 90%)`,
    ...(p.$interactive && {
      ':hover': {
        borderColor: theme.color.purple200,
      },
    }),
  },
}));
